import { useQuery } from '@tanstack/react-query';
import { Search, Store, ShoppingBag, TrendingUp, ArrowRight, Tag, Star } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import React from 'react'; // ✅ Para React.memo

import { NavbarPublic } from '../../components/NavbarPublic.jsx';
import { LazyImage } from '../../components/LazyImage.jsx'; // ✅ FASE 4: Lazy loading
import { prefetchRoute } from '../../utils/prefetch.js'; // ✅ FASE 4: Prefetch
import { api } from '@/lib/api.ts';
import { usePublicStorePricingRate, formatDualPrice } from '../../lib/dolar.ts';

// --- CONSTANTE DOMINIO R2 ---
const R2_DOMAIN = 'https://imagenes.tiendasya.store/';

// --- COMPONENTE DE FONDO AMBIENTAL OPTIMIZADO ---
const AmbientBackground = React.memo(() => (
  <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none bg-[#050505]">
    {/* ✅ OPTIMIZACIÓN: Blur reducido para mejor performance */}
    <div className="absolute top-[-20%] right-[0%] w-[70vw] h-[70vw] bg-indigo-900/10 rounded-full blur-[60px] opacity-60 mix-blend-multiply animate-pulse-slow" />
    <div className="absolute bottom-[-10%] left-[-10%] w-[60vw] h-[60vw] bg-emerald-900/10 rounded-full blur-[80px] opacity-50 mix-blend-multiply" />
  </div>
));

// --- TARJETA DE PRODUCTO DESTACADO ---
const ProductCard = React.memo(function ProductCard({ producto, onProductClick }) {
  const { data: pricingRate } = usePublicStorePricingRate(producto.store?.slug);
  
  const originalPriceUSD = producto.price;
  const discountedPriceUSD = producto.discounted_price;
  const hasDiscount = discountedPriceUSD < originalPriceUSD;
  const activePromotion = producto.active_promotion;
  
  const displayPriceUSD = hasDiscount ? discountedPriceUSD : originalPriceUSD;
  const dualPrice = formatDualPrice(displayPriceUSD, pricingRate);
  
  const totalStock = producto.variants?.length > 0 
    ? producto.variants.reduce((sum, v) => sum + (v.stock || 0), 0)
    : producto.inventory;

  let promotionBadge = null;
  if (activePromotion) {
    if (activePromotion.promotion_type === 'percentage') {
      promotionBadge = `-${activePromotion.discount_value}%`;
    } else if (activePromotion.promotion_type === 'fixed') {
      promotionBadge = `-$${activePromotion.discount_value}`;
    }
  }

  const handleMouseEnter = () => {
    // ✅ FIX: Solo hacer prefetch si tenemos slug válido
    if (producto.store?.slug) {
      prefetchRoute(`/t/${producto.store.slug}/producto/${producto.id}`);
    }
  };

  // ✅ FIX: Si no hay slug, mostrar error
  if (!producto.store?.slug) {
    return (
      <div className="group relative flex flex-col bg-[#0B0B0B] rounded-[24px] overflow-hidden border border-red-500/20 opacity-50 p-4">
        <div className="text-center">
          <span className="text-red-400 text-sm">Producto sin tienda válida</span>
          <p className="text-xs text-white/50 mt-1">{producto.name}</p>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={`/t/${producto.store?.slug}/producto/${producto.id}`}
      state={{ fromProduct: true }}
      onClick={onProductClick}
      className="group relative flex flex-col bg-[#0B0B0B] rounded-[24px] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-indigo-900/20 border border-white/5"
      onMouseEnter={handleMouseEnter}
    >
      {/* Badge Promoción */}
      {(promotionBadge || hasDiscount) && (
        <div className="absolute top-3 left-3 z-10">
          <span className="px-2 py-1 rounded-full bg-red-500 text-white text-xs font-bold">
            {promotionBadge || `-${Math.round(((originalPriceUSD - discountedPriceUSD) / originalPriceUSD) * 100)}%`}
          </span>
        </div>
      )}

      {/* Stock Badge */}
      {totalStock === 0 && (
        <div className="absolute top-3 right-3 z-10">
          <span className="px-2 py-1 rounded-full bg-red-500 text-white text-xs font-bold">
            AGOTADO
          </span>
        </div>
      )}

      {/* IMAGEN */}
      <div className="relative aspect-square w-full overflow-hidden bg-white flex items-center justify-center">
        <LazyImage
          src={producto.image || 'https://placehold.co/400x400/f8fafc/64748b?text=Producto'}
          alt={producto.name}
          className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-110"
          placeholder={
            <div className="w-full h-full flex items-center justify-center text-slate-400">
              <ShoppingBag className="h-16 w-16" />
            </div>
          }
        />
      </div>

      {/* INFO */}
      <div className="p-4 flex-1 flex flex-col">
        {/* TIENDA INFO - Más prominente */}
        <div className="flex items-center gap-2 mb-2">
          <Store className="h-3 w-3 text-indigo-400" />
          <span className="text-xs text-indigo-400 font-medium uppercase tracking-wider">
            {producto.store?.name || 'Tienda'}
          </span>
        </div>

        <h3 className="text-sm font-medium text-white mb-3 line-clamp-2 group-hover:text-indigo-400 transition-colors">
          {producto.name}
        </h3>
        
        <div className="mt-auto">
          {/* PRECIOS DUALES - Igual que en tiendas individuales */}
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-lg font-bold text-white">
              {dualPrice.usd}
            </span>
            {hasDiscount && (
              <span className="text-sm text-white/50 line-through">
                ${originalPriceUSD}
              </span>
            )}
          </div>
          
          {/* PRECIO EN MONEDA LOCAL - Más visible */}
          {pricingRate && (
            <span className="text-sm font-medium text-emerald-400 block mb-1">
              {dualPrice.local}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
});

// --- TARJETA DE TIENDA (Optimizada) ---
const StoreCard = React.memo(function StoreCard({ tienda }) {
  // LÓGICA DE LOGO CORREGIDA:
  const hasLogo = Boolean(tienda.logo);
  
  // Construimos la URL completa si existe el logo y es una ruta relativa
  let logoUrl = tienda.logo;
  if (hasLogo && !logoUrl.startsWith('http')) {
      // Evitamos doble slash si viene con uno al principio
      const cleanPath = logoUrl.startsWith('/') ? logoUrl.slice(1) : logoUrl;
      logoUrl = `${R2_DOMAIN}${cleanPath}`;
  }

  const handleMouseEnter = () => {
    prefetchRoute(`/t/${tienda.slug}`);
  };

  return (
    <Link
      to={`/t/${tienda.slug}`}
      className="group relative flex flex-col h-full p-1 rounded-[32px] transition-all duration-500 hover:-translate-y-2"
      onMouseEnter={handleMouseEnter}
    >
      <div className="absolute inset-0 rounded-[32px] bg-gradient-to-b from-indigo-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      <div className="relative flex flex-col h-full bg-[#0B0B0B] rounded-[30px] border border-white/5 overflow-hidden shadow-sm hover:shadow-2xl hover:shadow-indigo-900/20 transition-all duration-500">
        
        {/* HEADER DE LA TARJETA (IMAGEN O ICONO) */}
        <div className="relative h-40 bg-[#151515] flex items-center justify-center overflow-hidden group-hover:bg-[#1a1a1a] transition-colors duration-500">
           
           {/* Efecto de luz de fondo */}
           <div className="absolute w-24 h-24 bg-indigo-500/20 rounded-full blur-[40px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
           
           {hasLogo ? (
               // CASO 1: TIENDA CON LOGO
               <div className="relative z-10 w-24 h-24 rounded-full overflow-hidden border-4 border-[#0B0B0B] shadow-2xl group-hover:scale-110 transition-transform duration-500">
                   <img 
                      src={logoUrl} 
                      alt={`Logo de ${tienda.name}`} 
                      className="w-full h-full object-cover bg-white"
                   />
               </div>
           ) : (
               // CASO 2: TIENDA SIN LOGO (FALLBACK)
               <Store className="relative z-10 h-12 w-12 text-white/20 group-hover:text-white transition-colors duration-500 transform group-hover:scale-110" />
           )}
        </div>

        <div className="p-6 flex-1 flex flex-col">
          <h3 className="text-lg font-bold text-white mb-2 group-hover:text-indigo-400 transition-colors line-clamp-1">
            {tienda.name}
          </h3>
          <p className="text-sm text-white/50 line-clamp-2 mb-4 flex-1 font-light leading-relaxed">
            {tienda.description || 'Tienda oficial en TiendasYA Marketplace.'}
          </p>
          
          <div className="flex items-center justify-between pt-4 border-t border-white/5">
             <span className="text-[10px] font-bold tracking-widest uppercase text-white/30 group-hover:text-indigo-400 transition-colors">
                Visitar
             </span>
             <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-indigo-500 group-hover:text-white transition-all">
                <ArrowRight className="h-4 w-4" />
             </div>
          </div>
        </div>
      </div>
    </Link>
  );
});

export function MarketplacePage() {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('productos'); // productos | tiendas
  const [showOnlyPromotions, setShowOnlyPromotions] = useState(false); // ✅ NUEVO: Filtro de promociones
  const [selectedCategory, setSelectedCategory] = useState(null); // ✅ NUEVO: Categoría seleccionada
  const [currentPage, setCurrentPage] = useState(1); // ✅ PAGINACIÓN: Página actual para productos
  const [currentPageTiendas, setCurrentPageTiendas] = useState(1); // ✅ PAGINACIÓN: Página actual para tiendas

  // ✅ SCROLL RESTORATION: Restaurar posición al volver de un producto
  useEffect(() => {
    const savedScrollPosition = sessionStorage.getItem('marketplace-scroll-position');
    
    if (savedScrollPosition && location.state?.fromProduct) {
      // Restaurar scroll después de que el DOM esté listo
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedScrollPosition, 10));
        sessionStorage.removeItem('marketplace-scroll-position');
      }, 100);
    }
  }, [location]);

  // ✅ GUARDAR posición de scroll antes de navegar a un producto
  const handleProductClick = () => {
    sessionStorage.setItem('marketplace-scroll-position', window.scrollY.toString());
  };

  // ✅ NUEVAS QUERIES PARA DATOS REALES
  const { data: tiendasData, isLoading: loadingTiendas } = useQuery({
    queryKey: ['tiendas-publicas', currentPageTiendas],
    queryFn: async () => {
      try {
        // ✅ OPTIMIZADO: Endpoint con cache y paginación en backend
        const params = { page: currentPageTiendas };
        const { data } = await api.get('/api/stores/public', { params });
        return data; // Devolver objeto completo con count, next, previous, results
      } catch (error) {
        console.error('Error loading marketplace stores:', error);
        return { count: 0, results: [] };
      }
    },
    // ✅ OPTIMIZACIÓN: Cache de 60s en frontend (backend ya tiene cache de 60s)
    staleTime: 60000,           // Datos frescos por 1 minuto
    refetchInterval: false,     // No refetch automático (backend tiene cache)
    refetchOnWindowFocus: false, // No refetch al enfocar ventana
    refetchOnMount: false,      // No refetch al montar (usar cache)
    keepPreviousData: true,     // Mantener datos previos durante transiciones
  });

  const { data: productosData, isLoading: loadingProductos } = useQuery({
    queryKey: ['marketplace-productos', selectedCategory, currentPage],
    queryFn: async () => {
      try {
        // ✅ OPTIMIZADO: Endpoint directo con cache y paginación en backend
        const params = { page: currentPage };
        if (selectedCategory) params.category = selectedCategory;
        
        const { data } = await api.get('/api/marketplace/products', { params });
        return data; // Devolver objeto completo con count, next, previous, results
      } catch (error) {
        console.error('Error loading marketplace products:', error);
        return { count: 0, results: [] };
      }
    },
    // ✅ OPTIMIZACIÓN: Cache de 60s en frontend (backend ya tiene cache de 60s)
    staleTime: 60000,           // Datos frescos por 1 minuto
    refetchInterval: false,     // No refetch automático (backend tiene cache)
    refetchOnWindowFocus: false, // No refetch al enfocar ventana
    refetchOnMount: false,      // No refetch al montar (usar cache)
    keepPreviousData: true,     // Mantener datos previos durante transiciones
  });

  // Extraer productos y metadata de paginación
  const productos = productosData?.results || [];
  const totalProductos = productosData?.count || 0;
  const totalPages = Math.ceil(totalProductos / 24);

  // Extraer tiendas y metadata de paginación
  const tiendas = tiendasData?.results || [];
  const totalTiendas = tiendasData?.count || 0;
  const totalPagesTiendas = Math.ceil(totalTiendas / 24);

  const { data: promociones, isLoading: loadingPromociones } = useQuery({
    queryKey: ['marketplace-promociones'],
    queryFn: async () => {
      const { data } = await api.get('/api/marketplace/promotions');
      return data;
    },
    staleTime: 180000, // 3 minutos
  });

  const filteredTiendas = tiendas?.filter(tienda => {
    const matches = tienda.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tienda.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matches;
  }) || [];

  const filteredProductos = productos?.filter(producto => {
    // Filtro por búsqueda
    const matchesSearch = producto.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      producto.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      producto.store?.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // ✅ NUEVO: Filtro por categoría
    const matchesCategory = !selectedCategory || producto.category === selectedCategory;
    
    // ✅ NUEVO: Filtro por promociones
    if (showOnlyPromotions) {
      const hasDiscount = producto.discounted_price < producto.price;
      const hasActivePromotion = producto.active_promotion;
      const hasPromotionBadge = hasDiscount || hasActivePromotion;
      
      return matchesSearch && matchesCategory && hasPromotionBadge;
    }
    
    return matchesSearch && matchesCategory;
  }) || [];

  return (
    <div className="min-h-screen font-sans selection:bg-indigo-500/30 text-white">
      <AmbientBackground />
      <NavbarPublic />

      <main className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        
        {/* --- HERO SECTION --- */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
             <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
             <span className="text-[10px] font-bold tracking-widest uppercase text-white/70">
                El Futuro del Comercio Local
             </span>
          </div>
          
          <h1 className="text-5xl sm:text-7xl font-extralight tracking-tight text-white mb-6">
            Tiendas<span className="font-bold bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-purple-500">YA</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/60 max-w-2xl mx-auto font-light leading-relaxed">
            Una colección curada de las mejores tiendas y productos cerca de ti. Descubre, compara y compra en un solo lugar.
          </p>
        </div>

        {/* --- CATEGORÍAS POPULARES --- */}
        <section className="mb-24">
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: 'Moda', icon: '👕', category: 'Ropa y Moda' },
              { name: 'Tech', icon: '💻', category: 'Tecnología' },
              { name: 'Hogar', icon: '🏠', category: 'Hogar y Decoración' },
              { name: 'Deporte', icon: '⚽', category: 'Deportes y Fitness' },
              { name: 'Belleza', icon: '💄', category: 'Belleza y Cuidado Personal' },
              { name: 'Libros', icon: '📚', category: 'Libros y Educación' },
              { name: 'Gamer', icon: '🎮', category: 'Tecnología' }, // ✅ Mapeado a Tecnología
            ].map((categoria) => (
              <button
                key={categoria.name}
                onClick={() => {
                  // Si ya está seleccionada, deseleccionar
                  if (selectedCategory === categoria.category) {
                    setSelectedCategory(null);
                  } else {
                    setSelectedCategory(categoria.category);
                    setActiveTab('productos'); // Cambiar a productos
                    setShowOnlyPromotions(false); // Limpiar filtro de promociones
                    
                    // ✅ Scroll automático a la sección de productos
                    setTimeout(() => {
                      document.getElementById('marketplace-section')?.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'start'
                      });
                    }, 100);
                  }
                }}
                className={`group flex items-center gap-3 px-6 py-3 rounded-full border transition-all duration-300 ${
                  selectedCategory === categoria.category
                    ? 'bg-indigo-500 border-indigo-500 text-white'
                    : 'bg-white/5 border-white/10 hover:border-indigo-500/50 hover:bg-white/10'
                }`}
              >
                <span className="text-lg group-hover:scale-110 transition-transform">{categoria.icon}</span>
                <span className="text-xs font-bold uppercase tracking-wider">
                  {categoria.name}
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* --- OFERTAS DESTACADAS REALES --- */}
        <section className="mb-24">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-1.5 h-8 bg-red-500 rounded-full" />
            <h2 className="text-2xl font-light text-white">Ofertas Especiales</h2>
          </div>

          {loadingPromociones ? (
            <div className="grid gap-6 md:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-white/5 rounded-[32px] h-48 border border-white/5"></div>
              ))}
            </div>
          ) : promociones && promociones.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-3">
              {promociones.slice(0, 3).map((promo, idx) => {
                const gradients = [
                  'from-[#0a1f16] to-[#05110d]', // Verde
                  'from-[#0f111a] to-[#0a0b14]', // Azul
                  'from-[#1a0b16] to-[#11050e]'  // Púrpura
                ];
                const colors = [
                  { bg: 'bg-emerald-500/10', text: 'text-emerald-300', icon: 'bg-emerald-500/20' },
                  { bg: 'bg-indigo-500/10', text: 'text-indigo-300', icon: 'bg-indigo-500/20' },
                  { bg: 'bg-purple-500/10', text: 'text-purple-300', icon: 'bg-purple-500/20' }
                ];
                
                const gradient = gradients[idx % 3];
                const color = colors[idx % 3];
                
                return (
                  <div key={promo.id} className={`relative overflow-hidden rounded-[32px] bg-gradient-to-br ${gradient} border border-white/5 p-8 shadow-lg group hover:-translate-y-1 transition-transform`}>
                    <div className={`absolute top-0 right-0 w-40 h-40 ${color.bg} rounded-full blur-2xl -mr-10 -mt-10 group-hover:opacity-100 transition-opacity`} />
                    <div className="relative z-10">
                      <Tag className={`h-10 w-10 ${color.text} mb-4`} />
                      <h3 className="text-xl font-light text-white mb-2 line-clamp-1">{promo.title}</h3>
                      <p className="text-white/60 text-sm mb-6 font-light line-clamp-2">{promo.description}</p>
                      <span className={`inline-block px-4 py-2 rounded-full ${color.icon} ${color.text} text-xs font-bold uppercase tracking-widest`}>
                        {promo.promotion_type === 'percentage' ? `${promo.discount_value}% OFF` : 
                         promo.promotion_type === 'fixed' ? `$${promo.discount_value} OFF` : 
                         'OFERTA ESPECIAL'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Fallback a ofertas funcionales
            <div className="grid gap-6 md:grid-cols-3">
              {/* Card 1: Promociones y Descuentos */}
              <Link 
                to="#marketplace-section" 
                onClick={() => {
                  setActiveTab('productos');
                  setSearchTerm('');
                  setShowOnlyPromotions(true); // ✅ ACTIVAR filtro de promociones
                  // Scroll suave a la sección
                  setTimeout(() => {
                    document.getElementById('marketplace-section')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#0a1f16] to-[#05110d] border border-white/5 p-8 shadow-lg group hover:-translate-y-1 transition-transform cursor-pointer"
              >
                 <div className="absolute top-0 right-0 w-40 h-40 bg-emerald-500/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:bg-emerald-500/20 transition-colors" />
                 <div className="relative z-10">
                    <Tag className="h-10 w-10 text-emerald-400 mb-4" />
                    <h3 className="text-2xl font-light text-white mb-2">Promociones</h3>
                    <p className="text-white/60 text-sm mb-6 font-light">Descubre productos con descuentos especiales y ofertas limitadas.</p>
                    <div className="flex items-center justify-between">
                      <span className="inline-block px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-300 text-xs font-bold uppercase tracking-widest">
                         Ver Ofertas
                      </span>
                      <ArrowRight className="h-5 w-5 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                 </div>
              </Link>

              {/* Card 2: Nuevas Tiendas */}
              <Link 
                to="#marketplace-section" 
                onClick={() => {
                  setActiveTab('tiendas');
                  setSearchTerm('');
                  setShowOnlyPromotions(false); // ✅ DESACTIVAR filtro
                  // Scroll suave a la sección
                  setTimeout(() => {
                    document.getElementById('marketplace-section')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#0f111a] to-[#0a0b14] border border-white/5 p-8 shadow-lg group hover:-translate-y-1 transition-transform cursor-pointer"
              >
                 <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-500/10 rounded-full blur-2xl -ml-10 -mb-10 group-hover:bg-indigo-500/20 transition-colors" />
                 <div className="relative z-10">
                    <Store className="h-10 w-10 text-indigo-400 mb-4" />
                    <h3 className="text-2xl font-light text-white mb-2">Nuevas Tiendas</h3>
                    <p className="text-white/60 text-sm mb-6 font-light">Conoce las marcas locales que se unieron recientemente al marketplace.</p>
                    <div className="flex items-center justify-between">
                      <span className="inline-block px-4 py-2 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-bold uppercase tracking-widest">
                         Explorar
                      </span>
                      <ArrowRight className="h-5 w-5 text-indigo-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                 </div>
              </Link>

              {/* Card 3: Productos Destacados */}
              <Link 
                to="#marketplace-section" 
                onClick={() => {
                  setActiveTab('productos');
                  setSearchTerm('');
                  setShowOnlyPromotions(false); // ✅ DESACTIVAR filtro
                  // Scroll suave a la sección
                  setTimeout(() => {
                    document.getElementById('marketplace-section')?.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }}
                className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#1a0b16] to-[#11050e] border border-white/5 p-8 shadow-lg group hover:-translate-y-1 transition-transform cursor-pointer"
              >
                 <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-colors" />
                 <div className="relative z-10">
                    <Star className="h-10 w-10 text-purple-400 mb-4" />
                    <h3 className="text-2xl font-light text-white mb-2">Destacados</h3>
                    <p className="text-white/60 text-sm mb-6 font-light">Los productos más populares y mejor valorados del marketplace.</p>
                    <div className="flex items-center justify-between">
                      <span className="inline-block px-4 py-2 rounded-full bg-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-widest">
                         Ver Todo
                      </span>
                      <ArrowRight className="h-5 w-5 text-purple-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                 </div>
              </Link>
            </div>
          )}
        </section>

        {/* --- SECCIÓN PRINCIPAL CON TABS --- */}
        <section id="marketplace-section">
          <div className="flex flex-col md:flex-row items-end justify-between border-b border-white/5 pb-6 mb-12 gap-6">
            
            {/* Título y Tabs */}
            <div className="flex items-center gap-8">
               <div className="flex items-center gap-4">
                  <div className="w-1.5 h-10 bg-indigo-500 rounded-full" />
                  <div>
                      <h2 className="text-3xl font-light text-white leading-none">
                        Marketplace
                        {showOnlyPromotions && (
                          <span className="ml-3 px-3 py-1 text-sm bg-emerald-500/20 text-emerald-300 rounded-full font-medium">
                            Solo Promociones
                          </span>
                        )}
                        {selectedCategory && (
                          <span className="ml-3 px-3 py-1 text-sm bg-indigo-500/20 text-indigo-300 rounded-full font-medium">
                            {selectedCategory}
                          </span>
                        )}
                      </h2>
                      <p className="text-xs font-bold tracking-widest uppercase text-white/40 mt-2">
                         {activeTab === 'productos' ? 
                           `${filteredProductos.length} Productos` : 
                           `${filteredTiendas.length} Tiendas`
                         }
                      </p>
                  </div>
               </div>

               {/* Tabs */}
               <div className="flex bg-white/5 rounded-full p-1">
                  <button
                    onClick={() => {
                      setActiveTab('productos');
                      setShowOnlyPromotions(false); // ✅ DESACTIVAR filtro al cambiar tab
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeTab === 'productos' 
                        ? 'bg-indigo-500 text-white' 
                        : 'text-white/60 hover:text-white'
                    }`}
                  >
                    Productos
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('tiendas');
                      setShowOnlyPromotions(false); // ✅ DESACTIVAR filtro al cambiar tab
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      activeTab === 'tiendas' 
                        ? 'bg-indigo-500 text-white' 
                        : 'text-white/60 hover:text-white'
                    }`}
                  >
                    Tiendas
                  </button>
               </div>
            </div>

            {/* --- BUSCADOR (Dark Style) --- */}
            <div className="w-full md:w-auto">
                <div className="relative group w-full md:w-96">
                    {/* Fondo del buscador */}
                    <div className="absolute inset-0 bg-indigo-500/5 blur-lg rounded-full opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />
                    
                    <div className="relative flex items-center bg-[#111] border border-white/10 rounded-full px-5 py-3 shadow-sm focus-within:shadow-lg focus-within:border-indigo-500/50 transition-all">
                        <Search className="h-5 w-5 text-white/30 group-focus-within:text-indigo-500 transition-colors shrink-0" />
                        
                        <input
                          type="text"
                          placeholder={`Buscar ${activeTab}...`}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-white placeholder-white/30 text-sm font-medium tracking-wide ml-3 h-full"
                        />
                        
                        {searchTerm && (
                           <button onClick={() => setSearchTerm('')} className="ml-2 text-white/40 hover:text-white">
                              <span className="sr-only">Borrar</span>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                           </button>
                        )}
                    </div>
                </div>
            </div>
          </div>

          {/* ✅ BOTÓN LIMPIAR FILTRO DE PROMOCIONES */}
          {showOnlyPromotions && (
            <div className="flex justify-center mb-6">
              <button
                onClick={() => setShowOnlyPromotions(false)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition-all text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Mostrar todos los productos
              </button>
            </div>
          )}

          {/* ✅ BOTÓN LIMPIAR FILTRO DE CATEGORÍA */}
          {selectedCategory && (
            <div className="flex justify-center mb-6">
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20 transition-all text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Limpiar filtro de categoría
              </button>
            </div>
          )}

          {/* Contenido según tab activo */}
          {activeTab === 'productos' ? (
            // GRID DE PRODUCTOS
            <>
              {loadingProductos ? (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="animate-pulse bg-white/5 rounded-[24px] h-80 border border-white/5"></div>
                  ))}
                </div>
              ) : filteredProductos.length > 0 ? (
                <>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {filteredProductos.map((producto, index) => (
                      <ProductCard key={producto.id || index} producto={producto} onProductClick={handleProductClick} />
                    ))}
                  </div>

                  {/* ✅ PAGINACIÓN PROFESIONAL */}
                  {totalPages > 1 && (
                    <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-white/5 pt-8">
                      {/* Info de página */}
                      <div className="text-sm text-white/50">
                        Mostrando <span className="text-white font-medium">{((currentPage - 1) * 24) + 1}</span> - <span className="text-white font-medium">{Math.min(currentPage * 24, totalProductos)}</span> de <span className="text-white font-medium">{totalProductos}</span> productos
                      </div>

                      {/* Botones de paginación */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setCurrentPage(p => Math.max(1, p - 1));
                            setTimeout(() => {
                              const element = document.getElementById('marketplace-section');
                              if (element) {
                                const yOffset = -100; // Offset para dejar espacio arriba
                                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                window.scrollTo({ top: y, behavior: 'smooth' });
                              }
                            }, 0);
                          }}
                          disabled={currentPage === 1}
                          className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        >
                          Anterior
                        </button>

                        {/* Números de página */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => {
                                  setCurrentPage(pageNum);
                                  setTimeout(() => {
                                    const element = document.getElementById('marketplace-section');
                                    if (element) {
                                      const yOffset = -100; // Offset para dejar espacio arriba
                                      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                      window.scrollTo({ top: y, behavior: 'smooth' });
                                    }
                                  }, 0);
                                }}
                                className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${
                                  currentPage === pageNum
                                    ? 'bg-indigo-500 text-white'
                                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          onClick={() => {
                            setCurrentPage(p => Math.min(totalPages, p + 1));
                            setTimeout(() => {
                              const element = document.getElementById('marketplace-section');
                              if (element) {
                                const yOffset = -100; // Offset para dejar espacio arriba
                                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                window.scrollTo({ top: y, behavior: 'smooth' });
                              }
                            }, 0);
                          }}
                          disabled={currentPage === totalPages}
                          className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="py-32 text-center opacity-60 border border-dashed border-white/10 rounded-[32px] bg-white/5">
                  <div className="w-20 h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-6">
                     <ShoppingBag className="h-10 w-10 text-white/30" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {searchTerm ? 'Sin productos' : 'Próximamente productos'}
                  </h3>
                  <p className="text-white/40 max-w-md mx-auto mb-8">
                    {searchTerm ? `No encontramos productos que coincidan con "${searchTerm}".` : 'Los comerciantes están agregando sus productos. ¡Vuelve pronto!'}
                  </p>
                  {searchTerm && (
                     <button onClick={() => setSearchTerm('')} className="text-indigo-500 hover:text-indigo-400 font-semibold text-sm uppercase tracking-widest border-b border-indigo-500/30 hover:border-indigo-500">
                        Limpiar búsqueda
                     </button>
                  )}
                </div>
              )}
            </>
          ) : (
            // GRID DE TIENDAS
            <>
              {loadingTiendas ? (
                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="animate-pulse bg-white/5 rounded-[32px] h-80 border border-white/5"></div>
                  ))}
                </div>
              ) : filteredTiendas.length > 0 ? (
                <>
                  <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {filteredTiendas.map((tienda, index) => (
                      <StoreCard key={tienda.id || index} tienda={tienda} />
                    ))}
                  </div>

                  {/* ✅ PAGINACIÓN PROFESIONAL PARA TIENDAS */}
                  {totalPagesTiendas > 1 && (
                    <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-white/5 pt-8">
                      {/* Info de página */}
                      <div className="text-sm text-white/50">
                        Mostrando <span className="text-white font-medium">{((currentPageTiendas - 1) * 24) + 1}</span> - <span className="text-white font-medium">{Math.min(currentPageTiendas * 24, totalTiendas)}</span> de <span className="text-white font-medium">{totalTiendas}</span> tiendas
                      </div>

                      {/* Botones de paginación */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setCurrentPageTiendas(p => Math.max(1, p - 1));
                            setTimeout(() => {
                              const element = document.getElementById('marketplace-section');
                              if (element) {
                                const yOffset = -100; // Offset para dejar espacio arriba
                                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                window.scrollTo({ top: y, behavior: 'smooth' });
                              }
                            }, 0);
                          }}
                          disabled={currentPageTiendas === 1}
                          className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        >
                          Anterior
                        </button>

                        {/* Números de página */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPagesTiendas) }, (_, i) => {
                            let pageNum;
                            if (totalPagesTiendas <= 5) {
                              pageNum = i + 1;
                            } else if (currentPageTiendas <= 3) {
                              pageNum = i + 1;
                            } else if (currentPageTiendas >= totalPagesTiendas - 2) {
                              pageNum = totalPagesTiendas - 4 + i;
                            } else {
                              pageNum = currentPageTiendas - 2 + i;
                            }

                            return (
                              <button
                                key={pageNum}
                                onClick={() => {
                                  setCurrentPageTiendas(pageNum);
                                  setTimeout(() => {
                                    const element = document.getElementById('marketplace-section');
                                    if (element) {
                                      const yOffset = -100; // Offset para dejar espacio arriba
                                      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                      window.scrollTo({ top: y, behavior: 'smooth' });
                                    }
                                  }, 0);
                                }}
                                className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${
                                  currentPageTiendas === pageNum
                                    ? 'bg-indigo-500 text-white'
                                    : 'bg-white/5 text-white/70 hover:bg-white/10'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>

                        <button
                          onClick={() => {
                            setCurrentPageTiendas(p => Math.min(totalPagesTiendas, p + 1));
                            setTimeout(() => {
                              const element = document.getElementById('marketplace-section');
                              if (element) {
                                const yOffset = -100; // Offset para dejar espacio arriba
                                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                window.scrollTo({ top: y, behavior: 'smooth' });
                              }
                            }, 0);
                          }}
                          disabled={currentPageTiendas === totalPagesTiendas}
                          className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm font-medium hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        >
                          Siguiente
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="py-32 text-center opacity-60 border border-dashed border-white/10 rounded-[32px] bg-white/5">
                  <div className="w-20 h-20 mx-auto bg-white/10 rounded-full flex items-center justify-center mb-6">
                     <Store className="h-10 w-10 text-white/30" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {searchTerm ? 'Sin resultados' : 'Comienza tu viaje'}
                  </h3>
                  <p className="text-white/40 max-w-md mx-auto mb-8">
                    {searchTerm ? `No encontramos tiendas que coincidan con "${searchTerm}".` : 'Sé el primero en unirte a nuestra red exclusiva de comerciantes.'}
                  </p>
                  {!searchTerm && (
                    <Link to="/registro" className="inline-block px-8 py-3 rounded-full bg-indigo-600 text-white font-bold text-sm uppercase tracking-widest hover:bg-indigo-500 transition-colors shadow-lg shadow-indigo-500/30">
                      Crear Tienda
                    </Link>
                  )}
                  {searchTerm && (
                     <button onClick={() => setSearchTerm('')} className="text-indigo-500 hover:text-indigo-400 font-semibold text-sm uppercase tracking-widest border-b border-indigo-500/30 hover:border-indigo-500">
                        Limpiar búsqueda
                     </button>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        {/* Footer */}
        <footer className="mt-32 border-t border-white/5 pt-12 pb-8 text-center opacity-40 hover:opacity-100 transition-opacity duration-500">
           <p className="text-xs font-mono tracking-[0.2em] uppercase text-white/60">
              &copy; 2025 TiendasYA Marketplace. All Rights Reserved.
           </p>
        </footer>
      </main>
    </div>
  );
}